import PluginPreview from "./PluginPreview/PluginPreview";


const WebViewProps=[
    {
        name:"id",type:"string",
        description:"The webview id",
    },
    {
        name:"file",type:"string",
        description:`
            a filename with extension in your www folder
            @more prioritized than the url prop
        `,
    },
    {
        name:"url",type:"string",
        description:"A http/https url to show external sites",
        notice:`
            Please be careful when using this property because
            the target url will have access to cordova plugins
            @the need for this prop is extremely rare and should not be
            considered as a replacement for 
            <a href="https://github.com/apache/cordova-plugin-inappbrowser">cordova-plugin-inappbrowser</a>
        `,
    },
    {
        name:"statusBarTranslucent",type:"boolean",
        defaultValue:"false",
        description:`
            if True, the statusbar and the keybaord will overlay the webview.
            @Android only. For ios, use 
            <a href="https://github.com/apache/cordova-plugin-statusbar">cordova-plugin-statusbar</a> instead.
            @default to false
        `,
    },
    {
        name:"statusBarColor",type:"string",
        defaultValue:"white",
        description:"default to \"white\"",
    },
    {
        name:"backgroundColor",
        type:"string",
        defaultValue:"white",
        description:`The webview background color before loading the html file, default to "white"`,
    },
    {
        name:"showAnimation",
        type:`"slideLeft"|"slideUp"|"fadeIn"`,
        defaultValue:"slideLeft",
        description:`
            The webview animation when shown.
            @Applied only for non-modal webviews.
            @default to "slideLeft"
        `,
    },
    {
        name:"closeAnimation",
        type:`"slideDown"|"fadeOut"`,
        defaultValue:"fadeOut",
        description:`
            The webview animation when closed.
            @Applied only for non-modal webviews.
            @default to "fadeOut"
        `,
    },
    {
        name:"asModal",
        type:"boolean",
        description:"If true, shows the new webview with a modal animation.",
    },
    {
        name:"modalStyle",
        type:"object",
        description:"Only applied when asModal is true",
        props:[
            {
                name:"width",
                type:"number",
                description:`
                    width of the modal relative to the window width
                    @Value between 0 and 1
                    @Default to 1
                `,
            },
            {
                name:"height",
                type:"number",
                description:`
                    height of the modal relative to the window height
                    @Value between 0 and 1
                    @Default to 0.85
                `,
            },
            {
                name:"marginTop",
                type:"number",
                description:`
                    Top margin of the modal relative to the window height.
                    @Value between -1 and 1.
                `,
            },
            {
                name:"marginLeft",
                type:"number",
                description:`
                    Top margin of the modal relative to the window width.
                    @Value between -1 and 1.
                `,
            },
            {
                name:"verticalAlign",
                type:`"bottom"|"top"|"middle"`,
                description:`default to "middle"`,
            },
            {
                name:"opacity",
                type:"number",
                description:"default to 1",
            },
            {
                name:"silent",type:"boolean",
                description:`
                    If true, the modal pop up sound effect is cancelled.
                    @default to "false"
                `,
            },
            {
                name:"dismissible",type:"boolean",
                defaultValue:"true",
                description:"If true, the modal is dismissible via touch interactions",
            },
            {name:"notchColor",type:"string"},
            {
                name:"roundedTopLeftCorner",
                type:"boolean",
                defaultValue:"true",
            },
            {
                name:"roundedTopRightCorner",
                type:"boolean",
                defaultValue:"true",
            },
            {
                name:"roundedBottomLeftCorner",
                type:"boolean",
                defaultValue:"false",
            },
            {
                name:"roundedBottomRightCorner",
                type:"boolean",
                defaultValue:"false",
            },
        ],
    },
];

export default {
    id:"WebView",
    name:"vritra-plugin-webview",
    description:`
        Opens a new cordova activity for android, ViewController for ios.
        @the new webview will have access to all cordova plugins
        @defines a global <b>WebView</b> object
    `,
    preview:PluginPreview,
    methods:[
        {
            name:"defineWebViews",
            type:"(webviews:WebViewProps[],fallback:function):void",
            description:"",
            props:[
                {name:"fallback",type:"(message:String):void"},
                {
                    name:"WebViewProps",
                    type:"object",
                    props:WebViewProps,
                }
            ],
        },
        {
            name:"show",
            type:"(options:object):void",
            description:`
                Shows a new webview.
                @The shown webview will have access to all cordova plugins.
                @Overwrites the props defined for the webview with such id 
            `,
            props:[{
                name:"options",type:"object",
                props:[
                    {
                        name:"id",type:"string",
                        description:`
                            The target webview's id. 
                            @The defined webview props are applied as defaults.
                        `,
                    },
                    {
                        name:"message",
                        type:"string",
                        description:`
                            A message to pass to the new webiew.
                            @This message is freed when the new webview is closed
                            and the onClose method is called. 
                            @If message is not of type string, JSON.stringify is called.
                        `,
                    },
                    {
                        name:"onClose",
                        type:"(data:object):void",
                        props:[{
                            name:"data",
                            type:"object",
                            props:[
                                {
                                    name:"message",type:"string",
                                    description:`
                                        A message from the new webview.
                                        @If the new webview did not send a message, this value is the same
                                        as the message passed in the message property when showing the new
                                        webview. 
                                    `,
                                },
                                {
                                    name:"store",
                                    type:"object",
                                    description:"object shared between all webviews",
                                },
                            ],
                        }],
                    },
                    ...WebViewProps.filter(({name})=>name!=="id"),
                ],
            }],
        },
        {
            name:"useMessage",
            type:"(handler:function):void",
            description:"Uses the message from the webview that showed the current webview",
            props:[{
                name:"handler",
                type:"(message:string):void",
            }],
        },
        {
            name:"setMessage",
            type:"(message:String):void",
            description:`
                Sets the message value. 
                @if message is a falsy value, it's passed as an empty string.
                @if message is not a string, JSON.stringify is called.
            `,
        },
        {
            name:"initiateStore",
            type:"(store:Object,callback:function):void",
            description:"Sets the store value. Only objects are accepted",
            props:[
                {
                    name:"store",
                    type:"object",
                    description:"store initial value, default to an empty object",
                },
                {
                    name:"callback",
                    type:"(store:Object):void",
                }
            ],
        },
        {
            name:"useStore",
            type:"(handler:(store:Object)=>void)):void",
            description:`
                Uses the store object.
                @The store object passed to the handler is a value type.
                Setting it will not affect the store unless initiateStore is reused
                to overwrite the previous store value or call setStore instead. 
            `,
        },
        {
            name:"setStore",
            type:"(key:string,value:any,callback:(store:Object)=>void)",
            props:[
                {
                    name:"key",
                    type:"string",
                    description:`
                        the path to the value you want to set.
                        @ForArrays:
                        @1) array[*] will target all the array items.
                        @2) [index] => sets an array item at index.
                        @3) [last] => uses the last array item.
                        @UsableOnLastPathComponent :
                        @1) [push] => inserts the value at the end of an array.
                        @2) [unshift] => inserts the value at the start of an array.
                        @3) [pop] => removes the last item from an array, value property is ignored.
                        @4) [shift] => removes the first item from an array, value property is ignored.
                        @examples:
                        @"object.array[*][*].object.array[last]"
                        @"object.array[*].object.array[*].property"
                        @"object.array[0].array[4].array[last].property"
                    `,
                }
            ],
        },
        {
            name:"close",
            type:"(message:String):void",
            description:"Closes the current webview",
            props:[{
                name:"message",
                type:"string",
                description:`
                    The message to pass to the previous webview.
                    @if message is undefined, the value is ignored.
                    @if message is not a string, JSON.stringify is called.
                `,
            }],
            notice:`if called in the main webview, the app is minimized.`,
        },
    ],
}
